import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { baseApi } from 'store/api/baseApi';
import {
  useCreateSubscriptionMutation,
  useLazyGetSubscriptionStatusQuery
} from 'store/api/subscription.apislice';
import { useUpdateCustomerTypeMutation } from 'store/api/mutualFunds.apislice';
import {
  openInWebView,
  closeInAppBrowser,
  addBrowserListeners,
  removeBrowserListeners
} from 'utils/browser.utils';
import { isNative } from 'utils/platform.utils';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 100;

export const useSubscriptionPayment = () => {
  const dispatch = useDispatch();
  const [createSubscription, { isLoading: isCreating }] = useCreateSubscriptionMutation();
  const [triggerStatusQuery] = useLazyGetSubscriptionStatusQuery();
  const [updateCustomerType] = useUpdateCustomerTypeMutation();

  const [paymentUrl, setPaymentUrl] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [pollCount, setPollCount] = useState(0);

  const pollIntervalRef = useRef(null);
  const subscriptionIdRef = useRef(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const cleanup = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    removeBrowserListeners();
    setPaymentUrl(null);
  };

  const startPolling = (razorpaySubscriptionId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    subscriptionIdRef.current = razorpaySubscriptionId;
    setIsVerifying(true);
    setVerificationMessage('Verifying payment...');
    let attempts = 0;

    // Listen for browser close/nav events on native
    if (isNative()) {
      addBrowserListeners(
        // onClosed
        () => {
          console.log('Browser was closed. Polling continues...');
        },
        // onPageLoaded
        null,
        // onNavigationCompleted
        async (data) => {
          const url = data?.url || '';
          console.log('Navigation event in payment browser:', url);
          // Check success/failure URL matches
          if (url.match(/success|authorized|completed|authenticated/i)) {
            await closeInAppBrowser();
          } else if (url.match(/failed|failure|cancel/i)) {
            await closeInAppBrowser();
            cleanup();
            setIsVerifying(false);
            toast.error('Payment cancelled or failed.');
          }
        }
      );
    }

    pollIntervalRef.current = setInterval(async () => {
      attempts += 1;
      setPollCount(attempts);

      if (attempts > MAX_POLL_ATTEMPTS) {
        cleanup();
        setIsVerifying(false);
        await closeInAppBrowser();
        toast.error('Payment verification timed out. Please check your plan status.');
        return;
      }

      try {
        const response = await triggerStatusQuery(razorpaySubscriptionId).unwrap();
        const { status, paymentStatus, message, canClose } = response.result || {};

        if (message) {
          setVerificationMessage(message);
        }

        // Terminal Success states
        const isSuccess =
          status === 'ACTIVE' ||
          ['paid', 'success', 'active'].includes(paymentStatus?.toLowerCase());

        // Terminal Failure states
        const isFailure =
          status === 'FAILED' ||
          ['failed', 'failure'].includes(paymentStatus?.toLowerCase());

        // Cancelled/Expired states
        const isCancelled =
          status === 'CANCELLED' ||
          status === 'EXPIRED' ||
          ['cancelled', 'expired'].includes(paymentStatus?.toLowerCase());

        if (isSuccess) {
          // Sync user classification on the backend
          try {
            await updateCustomerType({ customerType: 'DIRECT' }).unwrap();
          } catch (err) {
            console.error('Failed to update customer type:', err);
          }

          // Invalidate cache tags
          dispatch(
            baseApi.util.invalidateTags([
              'Customer',
              'SubscriptionCurrent',
              'SubscriptionPaymentHistory'
            ])
          );

          // Add a short delay to allow database replication latency to finalize before hiding verification screen
          await new Promise((resolve) => setTimeout(resolve, 800));

          cleanup();
          await closeInAppBrowser();
          setIsVerifying(false);
          toast.success(message || 'Subscription activated successfully!');
        } else if (isFailure || isCancelled) {
          cleanup();
          await closeInAppBrowser();
          setIsVerifying(false);
          toast.error(message || 'Payment verification failed.');
        } else if (canClose) {
          // If backend suggests closing browser but hasn't reached terminal status yet
          await closeInAppBrowser();
        }
      } catch (error) {
        console.error('Error polling subscription status:', error);
      }
    }, POLL_INTERVAL_MS);
  };

  const initiatePayment = async (planId) => {
    try {
      const response = await createSubscription({ planId }).unwrap();
      const { shortUrl, razorpaySubscriptionId } = response.result || {};

      if (!shortUrl) {
        throw new Error('No checkout URL returned.');
      }

      setPaymentUrl(shortUrl);

      if (isNative()) {
        await openInWebView(shortUrl);
      }

      // Start verification polling loop
      startPolling(razorpaySubscriptionId);
    } catch (err) {
      console.error('Failed to initiate subscription payment:', err);
      toast.error(err?.data?.message || err?.message || 'Could not initiate upgrade flow.');
    }
  };

  const cancelVerification = async () => {
    cleanup();
    setIsVerifying(false);
    await closeInAppBrowser();
    toast.error('Payment verification cancelled.');
  };

  return {
    initiatePayment,
    isCreating,
    isVerifying,
    verificationMessage,
    pollCount,
    cancelVerification,
    paymentUrl
  };
};
